import { useEffect } from 'react';
import { Sentry } from '@/lib/sentry';

interface ErrorLogEntry {
  timestamp: string;
  error: string;
  stack?: string;
  url: string;
  userAgent: string;
  userId?: string;
}

class ErrorLogger {
  private static instance: ErrorLogger;
  private errors: ErrorLogEntry[] = [];
  private maxErrors = 50;

  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  logError(error: Error | string, additionalInfo?: Record<string, any>) {
    const errorEntry: ErrorLogEntry = {
      timestamp: new Date().toISOString(),
      error: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...additionalInfo
    };

    this.errors.push(errorEntry);
    
    // Keep only the most recent errors
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(-this.maxErrors);
    }

    // Send to Sentry
    if (error instanceof Error) {
      Sentry.captureException(error, {
        tags: {
          component: 'ErrorLogger',
        },
        extra: additionalInfo,
        contexts: {
          page: {
            url: errorEntry.url,
            timestamp: errorEntry.timestamp,
          },
        },
      });
    } else {
      Sentry.captureMessage(errorEntry.error, 'error');
    }

    // Send to analytics
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: errorEntry.error,
        fatal: false,
        custom_map: {
          url: errorEntry.url,
          timestamp: errorEntry.timestamp
        }
      });
    }

    // Log to console in development
    if (import.meta.env.MODE === 'development') {
      console.error('Error logged:', errorEntry);
    }
  }

  getErrors(): ErrorLogEntry[] {
    return [...this.errors];
  }

  clearErrors() {
    this.errors = [];
  }

  exportErrors(): string {
    return JSON.stringify(this.errors, null, 2);
  }
}

export const useErrorLogger = () => {
  const logger = ErrorLogger.getInstance();

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      logger.logError(event.error || event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      logger.logError(`Unhandled Promise Rejection: ${event.reason}`);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [logger]);

  return {
    logError: logger.logError.bind(logger),
    getErrors: logger.getErrors.bind(logger),
    clearErrors: logger.clearErrors.bind(logger),
    exportErrors: logger.exportErrors.bind(logger)
  };
};

export default ErrorLogger;
